/* Eloence UI — interactions: reveal-on-scroll, gallery, tabs, drawer, sticky CTA, etc.
   Event-delegated. Vanilla JS. No dependencies. */
(function () {
  'use strict';

  var FOCUSABLE_SEL = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var INIT_FLAG = 'elInit';

  /* Module-scope state — survives section re-renders (DOM nodes replaced, JS closure persists) */
  var _moduleInit = false;
  var _savedScrollY = 0;
  var _bodyLocked = false;

  function debug(e) {
    if (window.console && console.debug) console.debug('[eloence]', e);
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------- Body scroll lock (iOS-safe) ---------- */
  function lockBodyScroll() {
    if (_bodyLocked) return;
    _bodyLocked = true;
    _savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = '-' + _savedScrollY + 'px';
    document.body.classList.add('el-no-scroll');
  }

  function unlockBodyScroll() {
    if (!_bodyLocked) return;
    _bodyLocked = false;
    document.body.classList.remove('el-no-scroll');
    document.body.style.top = '';
    window.scrollTo(0, _savedScrollY);
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal(scope) {
    var revealRoot = (scope && scope.querySelectorAll) ? scope : document;
    if (!('IntersectionObserver' in window)) {
      revealRoot.querySelectorAll('.el-reveal').forEach(function (el) {
        el.classList.add('el-reveal--in');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('el-reveal--in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    revealRoot.querySelectorAll('.el-reveal').forEach(function (el) { io.observe(el); });

    /* Safety net: after 4 s, force any still-hidden reveal element into view.
       Guards against IntersectionObserver misses — e.g. fast scroll, print-layout
       passes, or screenshot tools that don't simulate scrolling. Cards 03/04 of
       transformation / whats-included / garantie / trust-badges were losing their
       titles because the observer never fired on them below the fold. */
    setTimeout(function () {
      revealRoot.querySelectorAll('.el-reveal:not(.el-reveal--in)').forEach(function (el) {
        el.classList.add('el-reveal--in');
      });
    }, 4000);
  }

  /* ---------- Gallery (swipe + dots + thumbs + optional autoplay) ---------- */
  function initGallery(root) {
    if (root.dataset[INIT_FLAG] === '1') return;
    root.dataset[INIT_FLAG] = '1';
    var track = root.querySelector('[data-el-gallery-track]');
    var slides = track ? track.querySelectorAll('[data-el-gallery-slide]') : [];
    var dots = root.querySelectorAll('[data-el-gallery-dot]');
    var thumbs = root.querySelectorAll('[data-el-gallery-thumb]');
    var prev = root.querySelector('[data-el-gallery-prev]');
    var next = root.querySelector('[data-el-gallery-next]');
    if (!track || slides.length === 0) return;

    var index = 0;
    var autoplayMs = parseInt(root.getAttribute('data-el-autoplay') || '0', 10);
    var autoplayTimer = null;

    function go(i) {
      index = (i + slides.length) % slides.length;
      track.style.transform = 'translateX(-' + (index * 100) + '%)';
      dots.forEach(function (d, di) { d.classList.toggle('is-active', di === index); });
      thumbs.forEach(function (t, ti) { t.classList.toggle('is-active', ti === index); });
    }

    function stopAutoplay() {
      if (autoplayTimer) { clearInterval(autoplayTimer); autoplayTimer = null; }
    }
    function startAutoplay() {
      if (autoplayMs >= 1000 && !autoplayTimer) {
        autoplayTimer = setInterval(function () { go(index + 1); }, autoplayMs);
      }
    }

    /* Expose autoplay control so module-level visibilitychange can pause/resume */
    root._elGalleryStop = stopAutoplay;
    root._elGalleryStart = startAutoplay;

    dots.forEach(function (d, di) { d.addEventListener('click', function () { stopAutoplay(); go(di); }); });
    thumbs.forEach(function (t, ti) { t.addEventListener('click', function () { stopAutoplay(); go(ti); }); });
    if (prev) prev.addEventListener('click', function () { stopAutoplay(); go(index - 1); });
    if (next) next.addEventListener('click', function () { stopAutoplay(); go(index + 1); });

    /* Touch swipe — passive handlers, 40px threshold */
    var startX = null;
    var startY = null;
    var isHorizontal = null;
    track.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isHorizontal = null;
    }, { passive: true });
    track.addEventListener('touchmove', function (e) {
      if (startX === null || isHorizontal !== null) return;
      var dx = Math.abs(e.touches[0].clientX - startX);
      var dy = Math.abs(e.touches[0].clientY - startY);
      if (dx > 10 || dy > 10) isHorizontal = dx > dy;
    }, { passive: true });
    track.addEventListener('touchend', function (e) {
      if (startX === null) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (isHorizontal && Math.abs(dx) > 40) {
        stopAutoplay();
        go(index + (dx < 0 ? 1 : -1));
      }
      startX = null; startY = null; isHorizontal = null;
    }, { passive: true });

    go(0);
    startAutoplay();
  }

  /* ---------- Tabs ---------- */
  function initTabs(root) {
    if (root.dataset[INIT_FLAG] === '1') return;
    root.dataset[INIT_FLAG] = '1';
    var tabs = root.querySelectorAll('[data-el-tab]');
    var panels = root.querySelectorAll('[data-el-panel]');
    if (tabs.length === 0) return;
    var keyboardNav = root.getAttribute('data-el-tab-keys') === 'true';

    function activate(id, focus) {
      tabs.forEach(function (t) {
        var active = t.getAttribute('data-el-tab') === id;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.setAttribute('tabindex', active ? '0' : '-1');
      });
      panels.forEach(function (p) {
        var active = p.getAttribute('data-el-panel') === id;
        p.classList.toggle('is-active', active);
        if (active) p.removeAttribute('hidden');
        else p.setAttribute('hidden', '');
      });
      if (focus) {
        var t = root.querySelector('[data-el-tab="' + id + '"]');
        if (t) t.focus();
      }
    }

    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { activate(t.getAttribute('data-el-tab')); });
      if (keyboardNav) {
        t.addEventListener('keydown', function (e) {
          var idx = -1;
          tabs.forEach(function (tt, ii) { if (tt === t) idx = ii; });
          if (idx < 0) return;
          var next = null;
          if (e.key === 'ArrowRight') next = (idx + 1) % tabs.length;
          else if (e.key === 'ArrowLeft') next = (idx - 1 + tabs.length) % tabs.length;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = tabs.length - 1;
          if (next !== null) {
            e.preventDefault();
            activate(tabs[next].getAttribute('data-el-tab'), true);
          }
        });
      }
    });
    activate(tabs[0].getAttribute('data-el-tab'));
  }

  /* Accordion: the theme uses native <details>/<summary> for open/close + a11y.
     Legacy JS binder was removed as dead code (see eloence-faq.liquid). */

  /* ---------- Quantity stepper (simple forms) ---------- */
  function initQty(root) {
    var input = root.querySelector('[data-el-qty-input]');
    if (!input) return;
    if (root.dataset[INIT_FLAG] === '1') return;
    root.dataset[INIT_FLAG] = '1';
    /* Accept both -minus/-plus (legacy) and -decr/-incr (PDP hero) selectors */
    var minus = root.querySelector('[data-el-qty-minus], [data-el-qty-decr]');
    var plus = root.querySelector('[data-el-qty-plus], [data-el-qty-incr]');
    function set(v) {
      var n = Math.max(1, parseInt(v || '1', 10));
      input.value = n;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (minus) minus.addEventListener('click', function () { set(parseInt(input.value, 10) - 1); });
    if (plus) plus.addEventListener('click', function () { set(parseInt(input.value, 10) + 1); });
  }

  /* ---------- Cart drawer open/close + focus trap ---------- */
  /* Drawer open/close are hoisted so module-level listeners can dispatch without
     relying on per-section state. `lookupDrawer()` finds the current DOM (survives
     section re-render). The Tab focus-trap is folded into the module-level
     keydown listener (initModuleOnce) instead of being added/removed per open. */
  var _drawerLastOpener = null;

  function lookupDrawer() {
    return document.querySelector('[data-el-cart-drawer]');
  }

  function drawerFocusables(panel) {
    return Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE_SEL))
      .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
  }

  function openDrawer() {
    var drawer = lookupDrawer();
    if (!drawer) return;
    var panel = drawer.querySelector('.el-cart-drawer__panel') || drawer;
    _drawerLastOpener = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockBodyScroll();
    /* Panier peut-etre modifie dans un autre onglet : remet le 5 ml offert
       d'aplomb. File unique cote eloence-gift.js : deux ouvertures rapides ne
       creent jamais deux lignes cadeau. */
    if (window.ELOENCE_GIFT) window.ELOENCE_GIFT.sync();
    setTimeout(function () {
      var focusables = drawerFocusables(panel);
      var close = panel.querySelector('[data-el-drawer-close]');
      (close || focusables[0] || panel).focus();
    }, 40);
  }

  function closeDrawer() {
    var drawer = lookupDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockBodyScroll();
    if (_drawerLastOpener && typeof _drawerLastOpener.focus === 'function') {
      try { _drawerLastOpener.focus(); } catch (e) { debug(e); }
    }
    _drawerLastOpener = null;
  }

  function initDrawer() {
    var drawer = lookupDrawer();
    if (!drawer) return;
    if (drawer.dataset[INIT_FLAG] === '1') return;
    drawer.dataset[INIT_FLAG] = '1';

    var panel = drawer.querySelector('.el-cart-drawer__panel') || drawer;
    var scrim = drawer.querySelector('[data-el-drawer-scrim]');

    /* Per-element listeners only — doc/window listeners live in initModuleOnce */
    if (scrim) scrim.addEventListener('click', closeDrawer);

    /* Close buttons inside panel (delegated — safe with innerHTML re-render) */
    panel.addEventListener('click', function (e) {
      if (e.target.closest('[data-el-drawer-close]')) {
        e.preventDefault();
        closeDrawer();
      }
    });
  }

  /* ---------- Render drawer body from cart state ---------- */
  function initDrawerRender() {
    var drawer = document.querySelector('[data-el-cart-drawer]');
    if (!drawer) return;
    if (drawer.dataset.elRenderInit === '1') return;
    drawer.dataset.elRenderInit = '1';

    var body = drawer.querySelector('[data-el-drawer-body]');
    var emptyEl = drawer.querySelector('[data-el-drawer-empty]');
    var filledEl = drawer.querySelector('[data-el-drawer-filled]');
    var countEl = drawer.querySelector('[data-el-drawer-count]');
    var subtotalEl = drawer.querySelector('[data-el-drawer-subtotal]');
    var progressFill = drawer.querySelector('[data-el-drawer-progress-fill]');
    var progressLabel = drawer.querySelector('[data-el-drawer-progress-label]');
    var progressWrap = drawer.querySelector('.el-cart-drawer__progress');

    /* Accept "80", "80,50", "80.50". Floor to cents. NaN → default 8000. */
    var rawThreshold = drawer.getAttribute('data-free-ship-threshold') || '80';
    var parsedThreshold = parseFloat(String(rawThreshold).replace(',', '.'));
    var progressThreshold = isNaN(parsedThreshold) ? 8000 : (parsedThreshold * 100) | 0;

    var currencySymbol = drawer.getAttribute('data-currency-symbol') || '€';
    var labelRemain = drawer.getAttribute('data-label-remaining') || 'Plus que [amount] pour la livraison offerte.';
    var labelUnlocked = drawer.getAttribute('data-label-unlocked') || 'Livraison offerte débloquée.';
    var removeLabel = drawer.getAttribute('data-label-remove') || 'Retirer';

    function fmt(cents) {
      return (cents / 100).toFixed(2).replace('.', ',') + '\u00a0' + currencySymbol;
    }

    function escapeHtml(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    /* Ligne 5 ml offert (assets/eloence-gift.js) : « Offert » + prix barre du
       5 ml payant, pas de quantite ni de bouton retirer, un selecteur de parfum. */
    function giftLineHtml(it, gift) {
      var info = gift.lineInfo(it) || {};
      var labels = gift.labels || {};
      var imgUrl = it.image || (it.featured_image && it.featured_image.url) || info.image || '';
      var options = gift.choices().map(function (c) {
        return '<option value="' + c.variantId + '"' + (c.variantId === it.variant_id ? ' selected' : '') +
          (!c.available && c.variantId !== it.variant_id ? ' disabled' : '') + '>' + escapeHtml(c.name) + '</option>';
      }).join('');
      var selectId = 'el-gift-select-' + escapeHtml(it.key).replace(/[^a-zA-Z0-9_-]/g, '');
      return (
        '<div class="el-drawer__line el-drawer__line--gift" data-key="' + escapeHtml(it.key) + '" data-el-gift-line>' +
          (imgUrl ? '<img class="el-drawer__img" src="' + escapeHtml(imgUrl) + '" alt="" loading="lazy" width="72" height="84">' : '<div class="el-drawer__img"></div>') +
          '<div class="el-drawer__info">' +
            '<h3 class="el-drawer__title">' + escapeHtml(labels.line || it.product_title) + '</h3>' +
            '<label class="el-drawer__gift-choose" for="' + selectId + '">' + escapeHtml(labels.choose || 'Choisir mon 5 ml') + '</label>' +
            '<select class="el-drawer__gift-select" id="' + selectId + '" data-el-gift-select>' + options + '</select>' +
          '</div>' +
          '<div class="el-drawer__side">' +
            '<div class="el-drawer__price el-drawer__price--gift">' +
              (info.paidPrice ? '<s class="el-drawer__gift-was">' + fmt(info.paidPrice) + '</s> ' : '') +
              '<span class="el-drawer__gift-free">' + escapeHtml(labels.free || 'Offert') + '</span>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }

    function lineHtml(it) {
      var gift = window.ELOENCE_GIFT;
      if (gift && gift.isGiftLine(it)) return giftLineHtml(it, gift);
      var imgUrl = it.image || (it.featured_image && it.featured_image.url) || '';
      var variantTitle = it.variant_title && it.variant_title !== 'Default Title' ? it.variant_title : '';
      return (
        '<div class="el-drawer__line" data-key="' + escapeHtml(it.key) + '">' +
          (imgUrl ? '<img class="el-drawer__img" src="' + escapeHtml(imgUrl) + '" alt="" loading="lazy" width="72" height="84">' : '<div class="el-drawer__img"></div>') +
          '<div class="el-drawer__info">' +
            '<h3 class="el-drawer__title">' + escapeHtml(it.product_title) + '</h3>' +
            (variantTitle ? '<div class="el-drawer__variant">' + escapeHtml(variantTitle) + '</div>' : '') +
            '<div class="el-drawer__qty" role="group" aria-label="Quantity">' +
              '<button type="button" data-el-line-minus aria-label="Diminuer">−</button>' +
              '<span data-el-line-qty>' + it.quantity + '</span>' +
              '<button type="button" data-el-line-plus aria-label="Augmenter">+</button>' +
            '</div>' +
          '</div>' +
          '<div class="el-drawer__side">' +
            '<div class="el-drawer__price">' + fmt(it.final_line_price) + '</div>' +
            '<button type="button" class="el-drawer__remove" data-el-line-remove>' + escapeHtml(removeLabel) + '</button>' +
          '</div>' +
        '</div>'
      );
    }

    function render(state) {
      /* Avant la première réponse de /cart.js, subscribe() pousse un panier vide
         factice : on garde le rendu serveur (pastille, tiroir) au lieu d'afficher 0. */
      if (window.ELOENCE_CART && window.ELOENCE_CART.isReady && !window.ELOENCE_CART.isReady()) return;
      /* Re-query every render — section re-renders replace DOM, cached refs go stale */
      var badgeEls = document.querySelectorAll('[data-el-cart-count]');
      if (countEl) countEl.textContent = state.item_count;
      badgeEls.forEach(function (b) {
        b.textContent = state.item_count;
        b.style.display = state.item_count > 0 ? '' : 'none';
      });

      if (state.item_count === 0) {
        if (emptyEl) emptyEl.style.display = '';
        if (filledEl) filledEl.style.display = 'none';
        if (subtotalEl) subtotalEl.textContent = fmt(0);
        return;
      }
      if (emptyEl) emptyEl.style.display = 'none';
      if (filledEl) filledEl.style.display = '';

      if (body) body.innerHTML = state.items.map(lineHtml).join('');

      if (subtotalEl) subtotalEl.textContent = fmt(state.total_price);

      var remaining = Math.max(0, progressThreshold - state.total_price);
      var progress = Math.min(100, (state.total_price / progressThreshold) * 100);
      if (progressFill) progressFill.style.width = progress + '%';
      /* Exactement 1 flacon 50 ml : le message d'incitation au Duo prend la
         place du texte de la barre de livraison (pas de second bandeau). */
      var gIncentive = window.ELOENCE_GIFT && window.ELOENCE_GIFT.labels && window.ELOENCE_GIFT.labels.incentive;
      var gOneBottle = gIncentive && window.ELOENCE_GIFT.bottleCount(state.items) === 1;
      if (progressWrap) progressWrap.classList.toggle('is-incentive', !!gOneBottle);
      if (progressLabel && gOneBottle) {
        progressLabel.innerHTML = '<strong>' + escapeHtml(gIncentive) + '</strong>';
        if (progressWrap) progressWrap.classList.remove('is-unlocked');
      } else if (progressLabel) {
        if (remaining === 0) {
          progressLabel.innerHTML = '<strong>' + escapeHtml(labelUnlocked) + '</strong>';
          if (progressWrap) progressWrap.classList.add('is-unlocked');
        } else {
          var safeLabel = escapeHtml(labelRemain);
          progressLabel.innerHTML = safeLabel.replace('[amount]', '<strong>' + fmt(remaining) + '</strong>');
          if (progressWrap) progressWrap.classList.remove('is-unlocked');
        }
      }

      /* Masque les suggestions déjà au panier. Chaque bloc choisit sa règle
         via data-hide-by : "product" (Cross-sell signatures), "variant"
         (Upsell variantes : le 5 ml reste proposé si le 50 ml est au panier)
         ou "none". Un bloc dont toutes les cartes sont masquées disparaît. */
      var inCartVariants = {};
      var inCartProducts = {};
      state.items.forEach(function (it) {
        inCartVariants[String(it.variant_id)] = true;
        inCartProducts[String(it.product_id)] = true;
      });
      drawer.querySelectorAll('[data-el-xsell]').forEach(function (region) {
        var mode = region.getAttribute('data-hide-by') || 'product';
        var visible = 0;
        region.querySelectorAll('[data-el-xsell-card]').forEach(function (card) {
          var hide = false;
          if (mode === 'variant') hide = !!inCartVariants[card.getAttribute('data-variant-id')];
          else if (mode === 'product') hide = !!(inCartVariants[card.getAttribute('data-variant-id')] || inCartProducts[card.getAttribute('data-product-id')]);
          card.style.display = hide ? 'none' : '';
          if (!hide) visible++;
        });
        region.hidden = visible === 0;
      });
      /* Des cartes ont pu apparaitre / disparaitre : etat des fleches. */
      vupUpdateAll();
    }

    /* Line qty interactions — promise-keyed lock prevents double-fire during network */
    var pending = null;

    if (body) {
      body.addEventListener('click', function (e) {
        if (pending) return;
        var line = e.target.closest('[data-key]');
        if (!line) return;
        var key = line.getAttribute('data-key');
        var qSpan = line.querySelector('[data-el-line-qty]');

        if (e.target.closest('[data-el-line-minus]')) {
          var q = parseInt(qSpan ? qSpan.textContent : '1', 10) - 1;
          var p1 = q <= 0 ? window.ELOENCE_CART.remove(key) : window.ELOENCE_CART.updateQty(key, q);
          pending = p1.catch(function (err) { debug(err); }).then(function () { pending = null; });
        } else if (e.target.closest('[data-el-line-plus]')) {
          var q2 = parseInt(qSpan ? qSpan.textContent : '1', 10) + 1;
          pending = window.ELOENCE_CART.updateQty(key, q2)
            .catch(function (err) { debug(err); })
            .then(function () { pending = null; });
        } else if (e.target.closest('[data-el-line-remove]')) {
          pending = window.ELOENCE_CART.remove(key)
            .catch(function (err) { debug(err); })
            .then(function () { pending = null; });
        }
      });
    }

    /* Choix du parfum du 5 ml offert : un remplacement dans la file cadeau,
       puis un seul rendu (via _setState). */
    if (body) {
      body.addEventListener('change', function (e) {
        var sel = e.target.closest('[data-el-gift-select]');
        if (!sel || !window.ELOENCE_GIFT) return;
        sel.disabled = true;
        window.ELOENCE_GIFT.swap(parseInt(sel.value, 10)).then(function () { sel.disabled = false; });
      });
    }

    /* Cross-sell / upsell add buttons (separate pending — different op than line qty).
       Délégué sur le tiroir : couvre tous les blocs [data-el-xsell], pas seulement le premier. */
    var xsellPending = null;
    if (drawer) {
      drawer.addEventListener('click', function (e) {
        if (xsellPending) return;
        var addBtn = e.target.closest('[data-el-xsell-add]');
        if (!addBtn) return;
        e.preventDefault();
        var vid = parseInt(addBtn.getAttribute('data-variant-id'), 10);
        if (!vid) return;
        addBtn.disabled = true;
        addBtn.classList.add('is-loading');
        addBtn.setAttribute('aria-busy', 'true');
        xsellPending = window.ELOENCE_CART.add(vid, 1).then(function () {
          addBtn.disabled = false;
          addBtn.classList.remove('is-loading');
          addBtn.removeAttribute('aria-busy');
          var card = addBtn.closest('[data-el-xsell-card]');
          if (card) {
            card.classList.add('is-just-added');
            setTimeout(function () { card.classList.remove('is-just-added'); }, 800);
          }
        }).catch(function (err) {
          debug(err);
          addBtn.disabled = false;
          addBtn.classList.remove('is-loading');
          addBtn.removeAttribute('aria-busy');
        }).then(function () { xsellPending = null; });
      });
    }

    if (window.ELOENCE_CART) window.ELOENCE_CART.subscribe(render);
  }

  /* ---------- Upsell variantes du tiroir : fleches precedent / suivant ----------
     La liste defile a l'horizontale ; une molette de souris ne le permet pas.
     Les fleches (visibles sur pointeur fin, cf. CSS de la section) avancent
     d'une carte. Le clic est delegue sur document : il survit aux rechargements
     de section dans l'editeur. L'etat (fleches masquees si tout tient,
     desactivees en bout de liste) se recalcule au defilement, au
     redimensionnement et a l'ouverture du tiroir — la liste n'a pas de
     largeur mesurable tant que le tiroir est ferme. */
  function vupListFor(nav) {
    var btn = nav.querySelector('[aria-controls]');
    return btn ? document.getElementById(btn.getAttribute('aria-controls')) : null;
  }

  function vupUpdate(nav) {
    var list = vupListFor(nav);
    if (!list) return;
    var max = list.scrollWidth - list.clientWidth;
    /* La liste a 8 px de padding (pour l'ombre des cartes) : le magnetisme la
       pose a 8 px au depart, pas a 0. Tolerance = padding + 2 px. */
    var pad = (parseFloat(getComputedStyle(list).paddingLeft) || 0) + 2;
    nav.hidden = !(list.clientWidth > 0 && max > pad);
    var prev = nav.querySelector('[data-el-vup-prev]');
    var next = nav.querySelector('[data-el-vup-next]');
    if (prev) prev.disabled = list.scrollLeft <= pad;
    if (next) next.disabled = list.scrollLeft >= max - pad;
  }

  function vupUpdateAll() {
    document.querySelectorAll('[data-el-vup-nav]').forEach(vupUpdate);
  }

  function initVupNav() {
    document.querySelectorAll('[data-el-vup-nav]').forEach(function (nav) {
      if (nav.dataset[INIT_FLAG] === '1') return;
      nav.dataset[INIT_FLAG] = '1';
      var list = vupListFor(nav);
      if (!list) return;
      list.addEventListener('scroll', function () { vupUpdate(nav); }, { passive: true });
      list.addEventListener('scrollend', function () { vupUpdate(nav); });
      vupUpdate(nav);
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-el-vup-prev], [data-el-vup-next]');
    if (!btn) return;
    var nav = btn.closest('[data-el-vup-nav]');
    var list = nav && vupListFor(nav);
    if (!list) return;
    var card = list.querySelector('[data-el-xsell-card]:not([style*="display: none"])');
    var gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    var step = card ? card.getBoundingClientRect().width + gap : list.clientWidth;
    var dir = btn.hasAttribute('data-el-vup-prev') ? -1 : 1;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollBy({ left: dir * step, behavior: reduce ? 'auto' : 'smooth' });
    /* Filet si 'scroll' / 'scrollend' ne partent pas (navigateur ancien). */
    setTimeout(function () { vupUpdate(nav); }, 450);
  });
  window.addEventListener('resize', vupUpdateAll, { passive: true });
  window.addEventListener('eloence:cart:open', function () { setTimeout(vupUpdateAll, 320); });

  /* ---------- Announce bar rotator ----------
     Opt-in only via [data-el-announce-rotate]. Default announce bar (desktop)
     renders all items inline per the JSX design — no rotation. Mobile variant
     (Phase 3.20) may opt into rotation. */
  function initAnnounce() {
    var bars = document.querySelectorAll('[data-el-announce][data-el-announce-rotate]');
    bars.forEach(function (bar) {
      if (bar.dataset[INIT_FLAG] === '1') return;
      bar.dataset[INIT_FLAG] = '1';
      var items = bar.querySelectorAll('[data-el-announce-item]');
      if (items.length <= 1) {
        if (items[0]) items[0].classList.add('is-active');
        return;
      }
      var i = 0;
      items[0].classList.add('is-active');
      var timer = null;

      function start() {
        if (timer) return;
        timer = setInterval(function () {
          items[i].classList.remove('is-active');
          i = (i + 1) % items.length;
          items[i].classList.add('is-active');
        }, 4000);
      }
      function stop() {
        if (timer) { clearInterval(timer); timer = null; }
      }

      /* Expose start/stop so the module-level visibilitychange handler can pause all bars */
      bar._elAnnounceStart = start;
      bar._elAnnounceStop = stop;
      start();
    });
  }

  /* ---------- PDP hero (thumbs, swatches, qty, form, variant event) ---------- */
  function initPdp(root) {
    if (root.dataset[INIT_FLAG] === '1') return;
    root.dataset[INIT_FLAG] = '1';

    /* Gallery: thumbs + dots + counter + touch swipe — all drive a single activateSlide().
       Matches MobGallery (mobile.jsx:135-225): opacity-swap transition, dots pagination,
       counter ("N / total"), touch handler with 40px threshold for prev/next. */
    var thumbs = root.querySelectorAll('[data-el-pdp-thumb]');
    var slides = root.querySelectorAll('[data-el-pdp-slide]');
    var dots = root.querySelectorAll('[data-el-pdp-dot]');
    var counterCurrent = root.querySelector('[data-el-pdp-counter-current]');
    var main = root.querySelector('[data-el-pdp-main]');
    var totalSlides = slides.length;
    var activeIndex = 0;
    var prefersReducedMotion = (typeof window.matchMedia === 'function')
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Pilule « inspire de » flottante : visible sur la premiere image et sur
       les images qu'une variante revendique via data-media-index, masquee sur
       tout le reste de la galerie. Sans image de variante l'ensemble se reduit
       a la premiere image, ce qui est le comportement demande par defaut. */
    var inspiredPill = root.querySelector('[data-el-pdp-inspired]');
    var pillSlides = {};
    if (inspiredPill) {
      pillSlides[0] = true;
      root.querySelectorAll('[data-el-pdp-variant-select] option').forEach(function (o) {
        var mi = parseInt(o.getAttribute('data-media-index'), 10);
        if (!isNaN(mi) && mi >= 0) pillSlides[mi] = true;
      });
    }

    function syncInspiredPill(idx) {
      if (!inspiredPill) return;
      inspiredPill.classList.toggle('is-hidden', !pillSlides[idx]);
    }

    /* Vidéo du slide actif : autoplay muet en boucle (pas de bouton play, pas de son).
       Les autres vidéos sont remises en pause et rembobinées — tous les slides
       restent dans le DOM (opacity toggle), donc sans ça une vidéo hors-champ
       continuerait de tourner en arrière-plan. */
    function syncVideos(idx) {
      var idxStr = String(idx);
      slides.forEach(function (s) {
        var video = s.querySelector('video');
        if (!video) return;
        if (s.getAttribute('data-el-pdp-slide') === idxStr) {
          if (!prefersReducedMotion) {
            var playPromise = video.play();
            if (playPromise && playPromise.catch) playPromise.catch(function () {});
          }
        } else if (!video.paused || video.currentTime > 0) {
          video.pause();
          video.currentTime = 0;
        }
      });
    }

    function activateSlide(i) {
      if (totalSlides === 0) return;
      var idx = ((i % totalSlides) + totalSlides) % totalSlides; /* wrap */
      activeIndex = idx;
      var idxStr = String(idx);
      thumbs.forEach(function (t) { t.classList.toggle('is-active', t.getAttribute('data-el-pdp-thumb') === idxStr); });
      slides.forEach(function (s) { s.classList.toggle('is-active', s.getAttribute('data-el-pdp-slide') === idxStr); });
      dots.forEach(function (d) {
        var active = d.getAttribute('data-el-pdp-dot') === idxStr;
        d.classList.toggle('is-active', active);
        if (active) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      if (counterCurrent) counterCurrent.textContent = String(idx + 1);
      syncInspiredPill(idx);
      syncVideos(idx);
    }

    syncInspiredPill(activeIndex);
    syncVideos(activeIndex);

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        activateSlide(parseInt(thumb.getAttribute('data-el-pdp-thumb'), 10));
      });
    });
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        activateSlide(parseInt(dot.getAttribute('data-el-pdp-dot'), 10));
      });
    });

    /* Touch swipe on main image (mobile): 40px dx threshold → next/prev. */
    if (main && totalSlides > 1) {
      var touchStartX = null;
      main.addEventListener('touchstart', function (e) {
        if (e.touches && e.touches[0]) touchStartX = e.touches[0].clientX;
      }, { passive: true });
      main.addEventListener('touchend', function (e) {
        if (touchStartX === null || !e.changedTouches || !e.changedTouches[0]) return;
        var dx = e.changedTouches[0].clientX - touchStartX;
        if (dx > 40) activateSlide(activeIndex - 1);
        else if (dx < -40) activateSlide(activeIndex + 1);
        touchStartX = null;
      }, { passive: true });
    }

    /* Swatches → variant select */
    var form = root.querySelector('[data-el-pdp-form]');
    if (!form) return;
    var select = form.querySelector('[data-el-pdp-variant-select]');
    var swatches = form.querySelectorAll('[data-el-pdp-swatch-input]');
    var submitBtn = form.querySelector('[data-el-pdp-submit]');
    var submitText = form.querySelector('[data-el-pdp-submit-text]');
    var submitTextMobile = form.querySelector('[data-el-pdp-submit-text-mobile]');
    var atcPriceEl = form.querySelector('[data-el-pdp-atc-price]');
    var priceEl = root.querySelector('[data-el-pdp-price]');
    /* Encadre de remboursement de l'echantillon : visible seulement quand le
       titre de la variante contient l'un des libelles declencheurs. */
    var promoEl = root.querySelector('[data-el-pdp-promo]');
    var promoTriggers = [];
    if (promoEl) {
      (promoEl.getAttribute('data-trigger') || '').split(',').forEach(function (t) {
        var v = t.replace(/\s+/g, ' ').trim().toLowerCase();
        if (v) promoTriggers.push(v);
      });
    }
    var compareEl = root.querySelector('[data-el-pdp-compare]');
    var saveEl = root.querySelector('[data-el-pdp-save]');
    var labelAdd = (submitText && submitText.textContent.trim()) || 'Ajouter au panier';
    /* Track first sync to suppress price-flash on initial render (Agent B) */
    var _elFirstSync = true;

    /* Inject an inline error element if none exists (can't edit pdp-hero.liquid from here) */
    var errorEl = form.querySelector('[data-el-pdp-error]');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.setAttribute('data-el-pdp-error', '');
      errorEl.setAttribute('role', 'alert');
      errorEl.hidden = true;
      errorEl.style.cssText = 'margin-top:10px;padding:10px 14px;border-radius:6px;background:rgba(109,18,31,0.08);color:var(--el-rouge,#6d121f);font-family:var(--el-font-editorial,serif);font-size:13px;';
      form.appendChild(errorEl);
    }

    function optionValuesFromSwatches() {
      var groups = {};
      swatches.forEach(function (inp) {
        var idx = inp.getAttribute('data-option-index');
        if (!groups[idx] && inp.checked) groups[idx] = inp.value;
      });
      return groups;
    }

    function findMatchingOption() {
      if (!select) return null;
      var chosen = optionValuesFromSwatches();
      var keys = Object.keys(chosen).sort();
      var target = keys.map(function (k) { return chosen[k]; }).join(' / ');
      var options = select.options;
      for (var i = 0; i < options.length; i++) {
        if (options[i].textContent.trim() === target) return options[i];
      }
      return null;
    }

    function emitVariantChange(opt) {
      if (!opt) return;
      var detail = {
        variant: {
          id: opt.value,
          title: opt.textContent.trim(),
          price: opt.getAttribute('data-price'),
          compare: opt.getAttribute('data-compare'),
          priceRaw: parseInt(opt.getAttribute('data-price-raw') || '0', 10),
          compareRaw: parseInt(opt.getAttribute('data-compare-raw') || '0', 10),
          available: opt.getAttribute('data-available') === 'true',
        },
      };
      /* Dispatch from root + bubble to window */
      root.dispatchEvent(new CustomEvent('eloence:variant-change', { detail: detail, bubbles: true }));
      window.dispatchEvent(new CustomEvent('eloence:variant-change', { detail: detail }));
      /* Price-flash: Agent B — brief .is-flash pulse on price when variant changes.
         Skip on initial sync (first call after section mount) to avoid a page-load flash. */
      if (priceEl && !_elFirstSync) {
        priceEl.classList.remove('is-flash');
        /* Force reflow so re-adding the class restarts any keyframe animation */
        // eslint-disable-next-line no-unused-expressions
        void priceEl.offsetWidth;
        priceEl.classList.add('is-flash');
        setTimeout(function () { priceEl.classList.remove('is-flash'); }, 500);
      }
      _elFirstSync = false;
    }

    function syncUi() {
      if (!select) return;
      var opt = select.options[select.selectedIndex];
      if (!opt) return;
      var price = opt.getAttribute('data-price');
      var compare = opt.getAttribute('data-compare');
      var compareRaw = parseInt(opt.getAttribute('data-compare-raw') || '0', 10);
      var priceRaw = parseInt(opt.getAttribute('data-price-raw') || '0', 10);
      var available = opt.getAttribute('data-available') === 'true';

      if (priceEl && price) priceEl.textContent = price;
      if (compareEl) {
        if (compareRaw && compareRaw > priceRaw) {
          compareEl.textContent = compare;
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (saveEl) {
        if (compareRaw && compareRaw > priceRaw) {
          var pct = Math.round((compareRaw - priceRaw) * 100 / compareRaw);
          saveEl.textContent = '-' + pct + '%';
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }
      /* En mode offre, le bouton (libelle, prix, disabled) appartient a
         eloence-offer.js : la variante de la page n'en decide plus. */
      if (submitBtn && !submitBtn.hasAttribute('data-el-offer-atc')) {
        submitBtn.disabled = !available;
        var pdpLabelAvail = submitBtn.getAttribute('data-label-available') || labelAdd;
        var pdpLabelAvailMobile = submitBtn.getAttribute('data-label-available-mobile') || pdpLabelAvail;
        var pdpLabelUnavail = submitBtn.getAttribute('data-label-unavailable') || 'Épuisé';
        if (submitText) {
          submitText.textContent = available ? pdpLabelAvail : pdpLabelUnavail;
        }
        if (submitTextMobile) {
          submitTextMobile.textContent = available ? pdpLabelAvailMobile : pdpLabelUnavail;
        }
        if (atcPriceEl) {
          atcPriceEl.textContent = available && price ? ' \u00b7 ' + price : '';
        }
      }
      /* Image de variante. Quand une variante porte une image assignee dans
         l'admin, la galerie se cale dessus. data-media-index vaut -1 si la
         variante n'en a pas : on laisse alors la galerie ou elle est plutot
         que de la ramener de force sur la premiere image. */
      if (promoEl && !promoEl.hasAttribute('data-always')) {
        var vTitle = opt.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
        var promoOn = false;
        for (var pi = 0; pi < promoTriggers.length; pi++) {
          if (vTitle.indexOf(promoTriggers[pi]) >= 0) { promoOn = true; break; }
        }
        promoEl.hidden = !promoOn;
      }

      var mediaIdx = parseInt(opt.getAttribute('data-media-index'), 10);
      if (!isNaN(mediaIdx) && mediaIdx >= 0 && mediaIdx < totalSlides && mediaIdx !== activeIndex) {
        activateSlide(mediaIdx);
      }

      emitVariantChange(opt);
    }

    swatches.forEach(function (inp) {
      inp.addEventListener('change', function () {
        var group = inp.getAttribute('data-option-index');
        form.querySelectorAll('[data-option-index="' + group + '"]').forEach(function (sib) {
          var label = sib.closest('.el-pdp__swatch');
          if (label) label.classList.toggle('is-active', sib === inp);
        });
        var fieldset = inp.closest('[data-el-pdp-option]');
        var valueSpan = fieldset && fieldset.querySelector('[data-el-pdp-option-value]');
        if (valueSpan) valueSpan.textContent = inp.value;

        var match = findMatchingOption();
        if (match && select) {
          select.value = match.value;
          syncUi();
        }
      });
    });

    /* Qty stepper (decr/incr) */
    var qty = root.querySelector('[data-el-qty]');
    if (qty && qty.dataset[INIT_FLAG] !== '1') {
      qty.dataset[INIT_FLAG] = '1';
      var decr = qty.querySelector('[data-el-qty-decr]');
      var incr = qty.querySelector('[data-el-qty-incr]');
      var qInput = qty.querySelector('[data-el-qty-input]');
      function setQty(n) { if (qInput) qInput.value = Math.max(1, n | 0); }
      if (decr && qInput) decr.addEventListener('click', function () { setQty(parseInt(qInput.value, 10) - 1); });
      if (incr && qInput) incr.addEventListener('click', function () { setQty(parseInt(qInput.value, 10) + 1); });
    }

    /* AJAX add — submit button gets is-loading for styling (Team A) */
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      errorEl.hidden = true;
      errorEl.textContent = '';
      if (!window.ELOENCE_CART) { form.submit(); return; }
      /* Selecteur d'offre : un seul POST avec le tableau d'items de la carte
         choisie. Jamais de repli sur la variante de la page (ce serait ajouter
         autre chose que ce qui est affiche). */
      var offerEl = form.querySelector('[data-el-offer]');
      var request;
      if (offerEl) {
        var offer = typeof offerEl._elOfferItems === 'function'
          ? offerEl._elOfferItems()
          : { error: 'Impossible d\'ajouter au panier. Rechargez la page.' };
        if (offer.error) {
          errorEl.textContent = offer.error;
          errorEl.hidden = false;
          return;
        }
        if (submitBtn) { submitBtn.disabled = true; submitBtn.classList.add('is-loading'); submitBtn.setAttribute('aria-busy', 'true'); }
        request = window.ELOENCE_CART.addItems(offer.items);
      } else {
        if (submitBtn) { submitBtn.disabled = true; submitBtn.classList.add('is-loading'); submitBtn.setAttribute('aria-busy', 'true'); }
        var fd = new FormData(form);
        var id = parseInt(fd.get('id'), 10);
        var q = parseInt(fd.get('quantity') || '1', 10);
        request = window.ELOENCE_CART.add(id, q);
      }
      request.then(function () {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.classList.remove('is-loading'); submitBtn.removeAttribute('aria-busy'); }
        window.ELOENCE_CART.openDrawer();
      }).catch(function (err) {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.classList.remove('is-loading'); submitBtn.removeAttribute('aria-busy'); }
        var msg = (err && err.message) || 'Impossible d\'ajouter au panier. Réessayez.';
        errorEl.textContent = msg;
        errorEl.hidden = false;
      });
    });

    syncUi();
  }

  /* ---------- Mobile menu (hamburger) ---------- */
  function initMobileMenu() {
    var trigger = document.querySelector('[data-el-menu-trigger]');
    var menu = document.querySelector('[data-el-menu]');
    if (!trigger || !menu) return;
    if (trigger.dataset[INIT_FLAG] === '1') return;
    trigger.dataset[INIT_FLAG] = '1';
    function setState(open) {
      menu.classList.toggle('is-open', open);
      if (open) lockBodyScroll();
      else unlockBodyScroll();
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      menu.setAttribute('aria-hidden', open ? 'false' : 'true');
    }
    trigger.addEventListener('click', function () {
      setState(!menu.classList.contains('is-open'));
    });
    var close = menu.querySelector('[data-el-menu-close]');
    if (close) close.addEventListener('click', function () { setState(false); });
    /* Expose close-state helper so module-level Escape handler can close the menu */
    menu._elMenuClose = function () { setState(false); };
  }

  /* ---------- Predictive search ---------- */
  function initPredictiveSearch() {
    /* Gracefully no-op when search markup is missing (gift card / password page etc.) */
    var input = document.querySelector('[data-el-search-input]');
    var dropdown = document.querySelector('[data-el-search-dropdown]');
    var panel = document.querySelector('[data-el-search-panel]');
    var form = document.querySelector('[data-el-search-form]');
    if (!input || !dropdown) return;
    if (input.dataset[INIT_FLAG] === '1') return;
    input.dataset[INIT_FLAG] = '1';

    var idleEl = dropdown.querySelector('[data-el-search-idle]');
    var idleHtml = idleEl ? idleEl.outerHTML : '';
    var controller = null;
    var debounceTimer = null;
    var activeIndex = -1;
    var lastQuery = '';

    function escapeHtml(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    function setExpanded(v) {
      input.setAttribute('aria-expanded', v ? 'true' : 'false');
    }

    function openPanel() {
      if (!panel) return;
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      document.body.classList.add('el-no-scroll');
      setExpanded(true);
      setTimeout(function () { input.focus(); }, 30);
    }

    function closePanel() {
      if (!panel) return;
      panel.setAttribute('aria-hidden', 'true');
      panel.hidden = true;
      document.body.classList.remove('el-no-scroll');
      setExpanded(false);
      activeIndex = -1;
    }

    /* Expose panel open/close + helpers for the module-level delegated click handler */
    panel && (panel._elSearchOpen = openPanel);
    panel && (panel._elSearchClose = closePanel);

    function renderIdle() {
      dropdown.innerHTML = idleHtml;
    }

    function renderError() {
      dropdown.innerHTML = '<div class="el-psearch__empty">Aucun résultat</div>';
    }

    function renderLoading() {
      dropdown.innerHTML = '<div class="el-psearch__loading">Recherche en cours…</div>';
    }

    function productItem(p) {
      var url = escapeHtml(p.url || '#');
      var title = escapeHtml(p.title || '');
      var price = p.price ? escapeHtml(p.price) : '';
      var vendor = escapeHtml(p.vendor || '');
      var img = p.featured_image && p.featured_image.url
        ? '<div class="el-psearch__thumb"><img src="' + escapeHtml(p.featured_image.url) + '" alt="" loading="lazy" width="52" height="52"></div>'
        : '<div class="el-psearch__thumb"></div>';
      return (
        '<li><a class="el-psearch__item" role="option" href="' + url + '" data-el-search-result>' +
          img +
          '<div class="el-psearch__meta">' +
            '<div class="el-psearch__title">' + title + '</div>' +
            (vendor ? '<div class="el-psearch__sub">' + vendor + '</div>' : '') +
          '</div>' +
          (price ? '<div class="el-psearch__price">' + price + '</div>' : '') +
        '</a></li>'
      );
    }

    function articleOrPageItem(item) {
      var url = escapeHtml(item.url || '#');
      var title = escapeHtml(item.title || '');
      var summary = item.summary ? escapeHtml(String(item.summary).replace(/<[^>]+>/g, '').slice(0, 90)) : '';
      return (
        '<li><a class="el-psearch__item" role="option" href="' + url + '" data-el-search-result>' +
          '<div class="el-psearch__meta">' +
            '<div class="el-psearch__title">' + title + '</div>' +
            (summary ? '<div class="el-psearch__sub">' + summary + '</div>' : '') +
          '</div>' +
        '</a></li>'
      );
    }

    function moneyFormat(cents) {
      if (typeof cents !== 'number') return '';
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }

    function render(data, q) {
      var res = (data && data.resources && data.resources.results) || {};
      var products = res.products || [];
      var articles = res.articles || [];
      var pages = res.pages || [];
      var queries = res.queries || [];

      if (!products.length && !articles.length && !pages.length) {
        dropdown.innerHTML =
          '<div class="el-psearch__empty">Aucun résultat pour « ' + escapeHtml(q) + ' »</div>' +
          '<a class="el-psearch__cta" href="' + escapeHtml('/search?q=' + encodeURIComponent(q)) + '" data-el-search-result role="option">' +
            'Voir tous les résultats <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>' +
          '</a>';
        return;
      }

      var html = '';

      if (queries.length) {
        var top = queries[0];
        var qText = escapeHtml(top.text || q);
        html +=
          '<a class="el-psearch__query-row" role="option" data-el-search-result href="' + escapeHtml('/search?q=' + encodeURIComponent(top.text || q)) + '">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3-3"/></svg>' +
            '<span>Rechercher « ' + qText + ' »</span>' +
          '</a>';
      }

      if (products.length) {
        var productHtml = products.slice(0, 4).map(function (p) {
          var priceStr = '';
          if (typeof p.price === 'number') priceStr = moneyFormat(p.price);
          else if (typeof p.price === 'string') priceStr = p.price;
          return productItem(Object.assign({}, p, { price: priceStr }));
        }).join('');
        html +=
          '<div class="el-psearch__group">' +
            '<div class="el-psearch__group-label">Produits</div>' +
            '<ul class="el-psearch__items">' + productHtml + '</ul>' +
          '</div>';
      }

      if (articles.length) {
        html +=
          '<div class="el-psearch__group">' +
            '<div class="el-psearch__group-label">Articles</div>' +
            '<ul class="el-psearch__items">' + articles.slice(0, 4).map(articleOrPageItem).join('') + '</ul>' +
          '</div>';
      }

      if (pages.length) {
        html +=
          '<div class="el-psearch__group">' +
            '<div class="el-psearch__group-label">Pages</div>' +
            '<ul class="el-psearch__items">' + pages.slice(0, 4).map(articleOrPageItem).join('') + '</ul>' +
          '</div>';
      }

      html +=
        '<a class="el-psearch__cta" href="' + escapeHtml('/search?q=' + encodeURIComponent(q)) + '" data-el-search-result role="option">' +
          'Voir tous les résultats <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>' +
        '</a>';

      dropdown.innerHTML = html;
    }

    function fetchResults(q) {
      if (controller) controller.abort();
      controller = new AbortController();
      var url = '/search/suggest.json'
        + '?q=' + encodeURIComponent(q)
        + '&resources[type]=product,article,page'
        + '&resources[limit]=4'
        + '&resources[options][unavailable_products]=hide';
      return fetch(url, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
        signal: controller.signal,
      }).then(function (r) {
        if (r.status === 429 || !r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
    }

    function onInput() {
      var q = input.value.trim();
      lastQuery = q;
      clearTimeout(debounceTimer);

      if (!q) {
        if (controller) controller.abort();
        renderIdle();
        activeIndex = -1;
        return;
      }

      debounceTimer = setTimeout(function () {
        var qAtFetch = q;
        renderLoading();
        fetchResults(qAtFetch).then(function (data) {
          if (qAtFetch !== lastQuery) return;
          render(data, qAtFetch);
          activeIndex = -1;
        }).catch(function (err) {
          if (err && err.name === 'AbortError') return;
          debug(err);
          if (qAtFetch !== lastQuery) return;
          renderError();
        });
      }, 220);
    }

    input.addEventListener('input', onInput);
    input.addEventListener('focus', function () { setExpanded(true); });

    function getOptions() {
      return Array.prototype.slice.call(dropdown.querySelectorAll('[data-el-search-result]'));
    }

    function highlight(i) {
      var opts = getOptions();
      opts.forEach(function (o, idx) {
        var active = idx === i;
        o.classList.toggle('is-active', active);
        if (active) {
          o.setAttribute('aria-selected', 'true');
          if (typeof o.scrollIntoView === 'function') {
            try { o.scrollIntoView({ block: 'nearest' }); } catch (e) { debug(e); }
          }
        } else {
          o.removeAttribute('aria-selected');
        }
      });
      activeIndex = i;
    }

    input.addEventListener('keydown', function (e) {
      var opts = getOptions();
      if (e.key === 'Escape') {
        if (panel && panel.getAttribute('aria-hidden') === 'false') {
          e.preventDefault();
          closePanel();
        }
      } else if (e.key === 'ArrowDown') {
        if (opts.length === 0) return;
        e.preventDefault();
        var n = activeIndex < opts.length - 1 ? activeIndex + 1 : 0;
        highlight(n);
      } else if (e.key === 'ArrowUp') {
        if (opts.length === 0) return;
        e.preventDefault();
        var p = activeIndex > 0 ? activeIndex - 1 : opts.length - 1;
        highlight(p);
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0 && opts[activeIndex]) {
          e.preventDefault();
          var href = opts[activeIndex].getAttribute('href');
          if (href) window.location.href = href;
        }
      }
    });

    /* Prevent form submit with empty query */
    if (form) {
      form.addEventListener('submit', function (e) {
        if (!input.value.trim()) {
          e.preventDefault();
        }
      });
    }

    renderIdle();
  }

  /* ==========================================================================
     Module-level one-time init: doc/window delegated listeners live HERE.
     Runs once per page load, not per section re-render. Theme-editor section
     re-renders replace DOM but keep this IIFE's closure — so these bindings
     never accumulate. */
  function initModuleOnce() {
    if (_moduleInit) return;
    _moduleInit = true;

    /* --- Gallery autoplay pause on tab hidden --- */
    document.addEventListener('visibilitychange', function () {
      document.querySelectorAll('[data-el-gallery]').forEach(function (g) {
        if (document.hidden && g._elGalleryStop) g._elGalleryStop();
        else if (g._elGalleryStart) g._elGalleryStart();
      });
      document.querySelectorAll('[data-el-announce][data-el-announce-rotate]').forEach(function (bar) {
        if (document.hidden && bar._elAnnounceStop) bar._elAnnounceStop();
        else if (bar._elAnnounceStart) bar._elAnnounceStart();
      });
    });

    /* --- Drawer: open/close events, keydown (escape + tab-trap), delegated triggers --- */
    window.addEventListener('eloence:cart:open', openDrawer);
    window.addEventListener('eloence:cart:close', closeDrawer);

    document.addEventListener('keydown', function (e) {
      var drawer = lookupDrawer();
      var drawerOpen = drawer && drawer.classList.contains('is-open');

      /* Tab focus-trap while drawer is open */
      if (e.key === 'Tab' && drawerOpen) {
        var panel = drawer.querySelector('.el-cart-drawer__panel') || drawer;
        var focusables = drawerFocusables(panel);
        if (focusables.length === 0) { e.preventDefault(); return; }
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }

      if (e.key !== 'Escape') return;
      if (drawerOpen) { closeDrawer(); return; }
      /* Also close mobile menu on Escape */
      var menu = document.querySelector('[data-el-menu]');
      if (menu && menu.classList.contains('is-open') && menu._elMenuClose) {
        menu._elMenuClose();
        return;
      }
      /* And predictive search panel */
      var searchPanel = document.querySelector('[data-el-search-panel]');
      if (searchPanel && searchPanel.getAttribute('aria-hidden') === 'false' && searchPanel._elSearchClose) {
        searchPanel._elSearchClose();
      }
    });

    document.addEventListener('click', function (e) {
      /* Drawer open trigger */
      var openTrigger = e.target.closest('[data-el-cart-open]');
      if (openTrigger) {
        e.preventDefault();
        openDrawer();
        return;
      }

      /* Predictive search open/close + outside-click */
      var searchOpen = e.target.closest('[data-el-search-trigger], [data-el-search-open]');
      var searchPanel = document.querySelector('[data-el-search-panel]');
      if (searchOpen && searchPanel && searchPanel._elSearchOpen) {
        e.preventDefault();
        searchPanel._elSearchOpen();
        return;
      }
      var searchClose = e.target.closest('[data-el-search-close]');
      if (searchClose && searchPanel && searchPanel._elSearchClose) {
        e.preventDefault();
        searchPanel._elSearchClose();
        return;
      }
      /* Outside-click closes search panel */
      if (searchPanel && searchPanel.getAttribute('aria-hidden') === 'false') {
        var inside = e.target.closest('[data-el-search-panel] .el-psearch__panel');
        if (!inside && !searchOpen && searchPanel._elSearchClose) {
          searchPanel._elSearchClose();
        }
      }
    });

  }

  ready(function () {
    initModuleOnce();
    initReveal();
    document.querySelectorAll('[data-el-gallery]').forEach(initGallery);
    document.querySelectorAll('[data-el-tabs]').forEach(initTabs);
    document.querySelectorAll('[data-el-qty]').forEach(initQty);
    document.querySelectorAll('[data-el-pdp]').forEach(initPdp);
    initDrawer();
    initDrawerRender();
    initVupNav();
    initAnnounce();
    initMobileMenu();
    initPredictiveSearch();
  });

  /* ---------- Editeur de theme : rechargement d'une section ----------
     Shopify remplace le DOM d'une section sans recharger la page. Les
     ecouteurs de initModuleOnce sont poses sur document et survivent au
     remplacement : on ne le rejoue jamais. Les inits par element, eux,
     doivent etre rejoues sur le sous-arbre neuf.

     Les rappeler est sans danger : chacun est garde par data-el-init
     (data-el-render-init pour le rendu du tiroir), donc un rappel sur un
     noeud inchange sort immediatement. initReveal n'a pas de garde — il
     cree un IntersectionObserver neuf a chaque appel — d'ou sa portee. */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target;
    if (!root || !root.querySelectorAll) return;
    initReveal(root);
    root.querySelectorAll('[data-el-gallery]').forEach(initGallery);
    root.querySelectorAll('[data-el-tabs]').forEach(initTabs);
    root.querySelectorAll('[data-el-qty]').forEach(initQty);
    root.querySelectorAll('[data-el-pdp]').forEach(initPdp);
    initDrawer();
    initDrawerRender();
    initVupNav();
    initAnnounce();
    initMobileMenu();
    initPredictiveSearch();
  });
})();

/* ========== Eloence UI — v2 widgets (ig-stories, pyramid cards) ==========
   Les onglets « Pourquoi » sont pilotes par sections/eloence-pourquoi.liquid,
   qui porte sa propre init et son propre ecouteur shopify:section:load. */
(function () {
  'use strict';

  var INIT_FLAG = 'elInit';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* IG Stories — click a circle to toggle is-playing class (for future video playback). */
  function initIgStories(root) {
    if (root.dataset[INIT_FLAG] === '1') return;
    root.dataset[INIT_FLAG] = '1';
    var stories = root.querySelectorAll('[data-el-ig-story]');
    stories.forEach(function (story) {
      story.addEventListener('click', function (e) {
        e.preventDefault();
        stories.forEach(function (s) { if (s !== story) s.classList.remove('is-playing'); });
        story.classList.toggle('is-playing');
      });
    });
  }

  /* Olfactive pyramid — click a card (desktop) or a tab button (mobile) to set it active.
     Desktop: all cards visible; clicking highlights via box-shadow (.is-active).
     Mobile: trois layouts au choix du marchand (section setting mobile_layout) —
       'tabs'     : une seule carte visible, pilotee par la barre d'onglets ;
       'stack'    : les trois cartes empilees ;
       'carousel' : rail scroll-snap horizontal + fleches optionnelles.
     La barre d'onglets n'est rendue qu'en mode 'tabs', les fleches qu'en
     'carousel' : les deux branches ci-dessous no-op quand leurs noeuds
     n'existent pas. */
  function initPyramid(root) {
    if (root.dataset[INIT_FLAG] === '1') return;
    root.dataset[INIT_FLAG] = '1';
    var cards = root.querySelectorAll('[data-el-pyramid-card]');
    var tabs  = root.querySelectorAll('[data-el-pyramid-tab]');

    function activate(idx) {
      cards.forEach(function (c) {
        c.classList.toggle('is-active', parseInt(c.getAttribute('data-el-pyramid-card'), 10) === idx);
      });
      tabs.forEach(function (t) {
        var on = parseInt(t.getAttribute('data-el-pyramid-tab'), 10) === idx;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });
    }

    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        activate(parseInt(card.getAttribute('data-el-pyramid-card'), 10));
      });
    });

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        activate(parseInt(tab.getAttribute('data-el-pyramid-tab'), 10));
      });
    });

    /* Fleches du carrousel mobile. On fait defiler le RAIL horizontalement
       (track.scrollTo) et jamais target.scrollIntoView : scrollIntoView deplace
       aussi le scroll vertical de la page quand la section n'est pas
       entierement visible — c'est le bug iPhone corrige dans
       eloence-testimonials (v1.0.5). */
    var track = root.querySelector('[data-el-pyramid-track]');
    var prevBtn = root.querySelector('[data-el-pyramid-prev]');
    var nextBtn = root.querySelector('[data-el-pyramid-next]');
    var cardList = Array.prototype.slice.call(cards);

    function currentSlide() {
      if (!track || cardList.length === 0) return 0;
      var trackLeft = track.getBoundingClientRect().left;
      var best = 0, bestDist = Infinity;
      cardList.forEach(function (c, i) {
        var d = Math.abs(c.getBoundingClientRect().left - trackLeft);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      return best;
    }

    function goToSlide(i) {
      if (!track || cardList.length === 0) return;
      if (i < 0) i = cardList.length - 1;
      if (i >= cardList.length) i = 0;
      var target = cardList[i];
      if (!target) return;
      var left = target.offsetLeft - track.offsetLeft;
      if (typeof track.scrollTo === 'function') {
        track.scrollTo({ left: left, behavior: 'smooth' });
      } else {
        track.scrollLeft = left;
      }
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { goToSlide(currentSlide() - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goToSlide(currentSlide() + 1); });
    /* Expose pour le pilotage par selection de bloc dans l'editeur (bas de fichier). */
    root._elPyramidGoTo = goToSlide;

    /* Keyboard navigation for tablist (arrows / Home / End). */
    var tablist = root.querySelector('[data-el-pyramid-tabs]');
    if (tablist) {
      tablist.addEventListener('keydown', function (e) {
        var current = Array.prototype.indexOf.call(tabs, document.activeElement);
        if (current < 0) return;
        var next = current;
        if (e.key === 'ArrowRight') next = (current + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        else return;
        e.preventDefault();
        tabs[next].focus();
        activate(next);
      });
    }
  }

  /* Optional: stagger-reveal delay. Uses data-el-reveal-delay="N" (ms) on .el-reveal elements. */
  function applyRevealDelays() {
    document.querySelectorAll('[data-el-reveal-delay]').forEach(function (el) {
      var ms = parseInt(el.getAttribute('data-el-reveal-delay'), 10);
      if (!isNaN(ms)) el.style.transitionDelay = ms + 'ms';
    });
  }

  ready(function () {
    document.querySelectorAll('[data-el-ig-stories]').forEach(initIgStories);
    document.querySelectorAll('[data-el-pyramid]').forEach(initPyramid);
    applyRevealDelays();
  });

  /* Rechargement d'une section dans l'editeur — voir la note de l'IIFE 1. */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target;
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-el-ig-stories]').forEach(initIgStories);
    root.querySelectorAll('[data-el-pyramid]').forEach(initPyramid);
    applyRevealDelays();
  });

  /* ---------- Editeur de theme : pilotage par selection de bloc ----------
     Shopify emet shopify:block:select sur l'element qui porte
     {{ block.shopify_attributes }}. Sans ecouteur, selectionner un bloc dans
     la barre laterale ne fait rien bouger dans l'apercu : c'est la raison
     pour laquelle les onglets de 'Pourquoi' semblent morts dans l'editeur
     alors qu'ils fonctionnent en ligne.

     Expose sur window.Eloence pour les sections qui portent leur propre
     bloc javascript. Chaque site d'appel DOIT tester la presence du helper :
     l'ordre d'execution entre ce fichier et le bundle compile des sections
     est un comportement observe de Shopify, pas un contrat. */
  window.Eloence = window.Eloence || {};

  window.Eloence.onBlockSelect = function (rootSelector, onSelect, onDeselect) {
    if (!window.Shopify || !window.Shopify.designMode) return;
    function resolve(e) {
      var el = e.target;
      if (!el || !el.closest) return null;
      return el.closest(rootSelector);
    }
    document.addEventListener('shopify:block:select', function (e) {
      var root = resolve(e);
      if (root && onSelect) onSelect(root, e.target, !!(e.detail && e.detail.load));
    });
    if (onDeselect) {
      document.addEventListener('shopify:block:deselect', function (e) {
        var root = resolve(e);
        if (root) onDeselect(root, e.target);
      });
    }
  };

  /* Ouvre le <details> correspondant au bloc selectionne et ferme les autres.
     Partage par eloence-faq et les blocs 'accordion' de eloence-pdp-hero. */
  window.Eloence.bindDetailsBlockSelect = function (rootSelector, detailsSelector) {
    window.Eloence.onBlockSelect(rootSelector, function (root, block) {
      var target = (block.matches && block.matches(detailsSelector))
        ? block
        : (block.closest ? block.closest(detailsSelector) : null);
      if (!target) return;
      Array.prototype.forEach.call(root.querySelectorAll(detailsSelector), function (d) {
        d.open = (d === target);
      });
    });
  };

  /* Editeur : selectionner une phase dans la barre laterale l'active dans
     l'apercu. Passe par le helper plutot que par un branchement direct : le
     helper resout la racine via e.target.closest() au moment de l'evenement,
     donc il survit aux re-rendus de section. Un branchement direct capturerait
     `root` en closure et pointerait sur un noeud detache apres re-rendu.
     Les cartes et onglets sont re-derives du root passe par le helper, pour la
     meme raison. */
  if (window.Eloence && window.Eloence.onBlockSelect) {
    window.Eloence.onBlockSelect('[data-el-pyramid]', function (root, block) {
      var card = (block.closest ? block.closest('[data-el-pyramid-card]') : null);
      if (!card) return;
      var idx = parseInt(card.getAttribute('data-el-pyramid-card'), 10);
      if (isNaN(idx)) return;
      /* En carrousel, basculer .is-active ne montre rien : les trois cartes sont
         deja visibles. Il faut faire defiler le rail jusqu'a la phase choisie. */
      if (root.classList.contains('el-pyramid--mob-carousel') && typeof root._elPyramidGoTo === 'function') {
        root._elPyramidGoTo(idx);
      }
      Array.prototype.forEach.call(root.querySelectorAll('[data-el-pyramid-card]'), function (c) {
        c.classList.toggle('is-active', parseInt(c.getAttribute('data-el-pyramid-card'), 10) === idx);
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-el-pyramid-tab]'), function (t) {
        var on = parseInt(t.getAttribute('data-el-pyramid-tab'), 10) === idx;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });
    });
  }

})();
/* resync-nudge 2026-09-29T2 */
